import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bookmark, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/hooks/useArticleAnalytics";
import { ScenicBtcArt } from "./ScenicBtcArt";
import { H3, h3Age } from "./h3";

/* Hero 3.0 Lead Story — Figma 214:71 (1440) / 205:22 (768) / 206:17 (390).
   Media priority (founder rule): eligible live article media → approved
   NewsLayer scenic artwork → the scenic art itself is deterministic. Real
   save/share via the production account endpoints. */
export const LeadStory = ({ hero }: { hero: any }) => {
  const { user, refreshUser } = useAuth() as any;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [mediaFailed, setMediaFailed] = useState(false);
  if (!hero) return null;

  const mediaSrc = hero?.lead_visual?.url || hero?.hero_image_url || null;
  const useLiveMedia = !!mediaSrc && !mediaFailed;
  const saved = !!user?.saved_articles?.includes(hero.id);
  const kicker = (hero.display_kicker || hero.kicker || hero.content_type || (hero.categories || [])[0] || "NEWS")
    .toString().replace(/[_-]/g, " ").toUpperCase();
  const dek = hero.display_dek || hero.summary || hero.raw_excerpt;
  const byline = (hero.author_name || hero.source_name || "NEWSLAYER").toString().toUpperCase();

  const toggleSave = async () => {
    if (!user) { toast.info("Sign in to save stories"); navigate("/login"); return; }
    if (busy) return;
    setBusy(true);
    try {
      if (saved) await api.delete(`/account/saved/${hero.id}`);
      else await api.post(`/account/saved/${hero.id}`);
      await refreshUser?.();
      qc.invalidateQueries({ queryKey: ["saved"] });
      trackEvent("article_save", { slug: hero.slug, saved: !saved });
      toast.success(saved ? "Removed from saved" : "Saved to your account");
    } catch {
      toast.error("Could not update saved stories");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/article/${hero.slug}`;
    try {
      if (navigator.share) await navigator.share({ title: hero.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
      trackEvent("article_share", { slug: hero.slug });
    } catch { /* user cancelled */ }
  };

  const RoundBtn = ({ onClick, label, testid, active, children }: any) => (
    <button type="button" onClick={onClick} disabled={busy && label.includes("aved")} aria-label={label} data-testid={testid}
      className="w-[44px] h-[44px] md:w-[38px] md:h-[38px] xl:w-[40px] xl:h-[40px] rounded-full flex items-center justify-center transition-colors hover:bg-[#17082e]"
      style={{ background: "#05070b", border: `1px solid ${active ? H3.violetText : H3.violet}` }}>
      {children}
    </button>
  );

  return (
    <article data-testid="h3-lead-story"
      className="relative rounded-[10px] xl:rounded-[6px] overflow-hidden min-h-[480px] md:min-h-[409px] xl:min-h-[351px] min-[1440px]:min-h-[395px]"
      style={{ background: H3.card, border: `1px solid ${H3.border}` }}>
      <div className="absolute inset-0" aria-hidden>
        {useLiveMedia ? (
          <img src={mediaSrc} alt="" loading="eager" decoding="async" fetchPriority={"high" as any}
            className="w-full h-full object-cover" onError={() => setMediaFailed(true)} />
        ) : (
          <ScenicBtcArt />
        )}
      </div>
      {/* Left text vignette (215:70) + ground fade (215:6) */}
      <div className="absolute inset-0 hidden md:block" aria-hidden
        style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.45) 42%, rgba(0,0,0,0) 68%)" }} />
      <div className="absolute inset-0 md:hidden" aria-hidden
        style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.94) 0%, rgba(0,0,0,0.35) 66%, rgba(0,0,0,0) 100%)" }} />
      <div className="absolute inset-x-0 bottom-0 h-[27%]" aria-hidden
        style={{ background: "linear-gradient(180deg, transparent 0%, rgba(3,3,4,0.9) 100%)" }} />

      <div className="relative p-[16px] md:p-[23px] xl:p-[24px] flex flex-col items-start h-full">
        <span className="nl-mono inline-block w-fit rounded-[4px] md:rounded-[5px] px-[10px] py-[5px] text-[9px] md:text-[9.5px] leading-[13px] tracking-[0.08em]"
          style={{ background: H3.violetBg, border: `1px solid ${H3.violet}`, color: H3.purple }}
          data-testid="h3-lead-kicker">
          {kicker}
        </span>
        <h1 className="nl-sg text-[28px] leading-[35px] md:text-[36px] md:leading-[44px] xl:text-[30px] xl:leading-[37px] min-[1440px]:text-[34px] min-[1440px]:leading-[42px] font-bold mt-[14px] md:mt-[16px] max-w-[326px] md:max-w-[400px] xl:max-w-[400px] min-[1440px]:max-w-[440px] line-clamp-3"
          style={{ color: H3.text }} data-testid="h3-lead-title">
          {hero.title}
        </h1>
        {dek && (
          <p className="nl-geist text-[14px] leading-[18px] md:text-[15px] md:leading-[23px] xl:text-[13px] xl:leading-[20px] min-[1440px]:text-[15px] min-[1440px]:leading-[22px] mt-[12px] max-w-[314px] md:max-w-[366px] xl:max-w-[360px] min-[1440px]:max-w-[395px] line-clamp-3"
            style={{ color: H3.body }}>
            {dek}
          </p>
        )}
        <p className="nl-mono text-[9px] md:text-[10px] leading-[14px] mt-[14px] md:mt-[16px]" style={{ color: H3.meta }} data-testid="h3-lead-meta">
          {byline} • {h3Age(hero.published_at).toUpperCase()}{hero.read_minutes ? ` • ${hero.read_minutes} MIN READ` : ""}
        </p>
        <div className="flex items-center gap-[14px] md:gap-[13px] xl:gap-[16px] mt-[14px] md:mt-[16px]">
          <Link to={`/article/${hero.slug}`} data-testid="h3-lead-read"
            className="nl-geist inline-flex items-center h-[44px] md:h-[38px] xl:h-[40px] px-[16px] rounded-[8px] md:rounded-[6px] text-[12px] md:text-[11px] font-bold transition-transform duration-200 hover:-translate-y-[2px]"
            style={{ background: H3.violetDeep, border: `1px solid ${H3.violet}`, color: H3.text }}>
            READ FULL STORY →
          </Link>
          <RoundBtn onClick={toggleSave} label={saved ? "Remove from saved" : "Save story"} testid="h3-lead-save" active={saved}>
            <Bookmark size={15} color={saved ? H3.violetText : H3.text} fill={saved ? H3.violetText : "none"} />
          </RoundBtn>
          <RoundBtn onClick={share} label="Share story" testid="h3-lead-share">
            <Share2 size={15} color={H3.text} />
          </RoundBtn>
        </div>
        <p className="md:hidden nl-mono text-[8px] leading-[12px] mt-[18px] tracking-[0.04em]" style={{ color: H3.grayTouch }}
          data-testid="h3-lead-artnote">
          LIVE ARTICLE MEDIA → CURATED NEWSLAYER ART → DETERMINISTIC FALLBACK
        </p>
      </div>
    </article>
  );
};
